/** Phase D texts (classroom). Each package owns its own namespace; labels live in the sub-tables. */
/*
 * Translators: everything here is UI language EXCEPT the `phrases` table and the
 * `contest.words` list, which are English practice and must stay in English.
 */
export const enClassroom = {
  classroom: {
    mapNames: {} as Record<string, string>,
    zones: {} as Record<string, string>,
    objects: {
      cls_lostBox: 'Lost & found box',
      cls_notice: 'Notice board',
      cls_ball: 'Playground ball',
    },
    npc: {
      xiaohua: 'Xiaohua',
      xiaomei: 'Xiaomei',
      azhe: 'Azhe',
      xiaoqiang: 'Xiaoqiang',
      xiaofang: 'Xiaofang',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    /** English key phrases, one per case/event. ENGLISH PRACTICE: keep in English in every locale. */
    phrases: {
      late: 'Sorry I’m late. It won’t happen again.',
      homework: 'Excuse me, could you help me? I don’t understand this part.',
      standing: 'I’m sorry for talking. I’ll raise my hand next time.',
      ball: 'I feel sad when I don’t get a turn.',
      forgotHomework: 'I’m sorry, I forgot my homework. I’ll bring it next time.',
      misunderstanding: 'Excuse me, I think there’s a misunderstanding.',
      bullying: 'Stop, please. That’s not kind.',
      contest: 'Could you repeat the word, please?',
    },

    ui: {
      sayIt: '🔊 Say it in English',
      practised: '🗣️ English phrase practised!',
      journalMenu: '📔 Growth record',
      journalTitle: '📔 Growth record',
      tabPeople: 'Friends & family',
      tabLog: 'Moments',
      tabHobbies: 'Hobbies',
      family: 'Family',
      classmates: 'Classmates & friends',
      noLog: 'Nothing written yet. Go and live your days — kind choices, brave moments and even mistakes all end up here.',
      noHobbies: 'No hobbies practised yet. Try the piano, the easel, the field or the library!',
      hobbyLevel: (n: number) => `Lv ${n}`,
      hobbyPoints: (n: number) => `${n} sessions`,
      dayLabel: (week: number, day: string) => `Week ${week} · ${day}`,
      todayRow: 'Today’s moments',
      noticeTitle: '📌 Notice board',
      noticeNone: 'Nothing special today. Have a great day at school!',
      noticeCases: 'Some classmates may need a friend today. Keep your eyes open!',
      lateGone: 'Xiaohua already went in. You can still be kind to him later.',
      later: 'Talk later',
      back: '‹ Back',
      tryAgain: 'Hmm, try another way.',
      narrator: 'Meanwhile…',
      ballIdle: 'A scuffed football with “小強” written on it in marker. Xiaoqiang must have left it here.',
      chat: '💬 Chat',
      giveBack: (what: string) => `🎁 Give back ${what}`,
      coolHello: ['Oh… hi.', 'Hm. Hello.', '…Hi.'],
      warmHello: ['Hey, best buddy!', 'Yay, it’s you!', 'There you are! I was hoping to see you.'],
    },

    /** Affection hints next to the bars. */
    love: ['Strangers', 'Getting to know', 'Friends', 'Good friends', 'Best friends'],
    loveFamily: ['Distant', 'Okay', 'Close', 'Very close', 'Inseparable'],

    hobbies: {
      music: '🎵 Music', art: '🎨 Art', sport: '⚽ Sport', reading: '📚 Reading', nature: '🌿 Nature', craft: '✂️ Crafts',
      cooking: '🍳 Cooking', garden: '🌱 Gardening', english: '🗣️ English', dance: '💃 Dance',
    } as Record<string, string>,

    /** Growth-record lines (`logJournal(icon, key, arg?)`). */
    journalKeys: {
      helpedLate: () => 'Helped Xiaohua say sorry for being late — and gave him an alarm-clock tip',
      lateExcuse: () => 'Told Xiaohua to make up an excuse. It didn’t go well.',
      teasedLate: () => 'Laughed at Xiaohua when he was late',
      helpedHomework: () => 'Helped Xiaomei ask the teacher for help, honestly',
      copiedHomework: () => 'Let Xiaomei copy my homework. She still didn’t understand it.',
      skippedHomework: () => 'Told Xiaomei to pretend to be sick',
      helpedStanding: () => 'Helped Azhe keep calm and make it right with Ms. Lin',
      clowned: () => 'Made funny faces with Azhe in class — we both got told off',
      mockedStanding: () => 'Said “serves you right” to Azhe',
      ballMadeUp: () => 'Helped Xiaoqiang and Xiaofang use “I feel…” words and take turns',
      ballSide: (who?: string) => `Took ${who ?? 'one'}’s side in the ball fight`,
      ballGrabbed: () => 'Grabbed the ball and ran off. Nobody laughed.',
      madeUp: (who?: string) => `Said sorry to ${who ?? 'a friend'} and made up`,
      forgotHonest: () => 'Forgot my homework and told the teacher the truth',
      forgotExcuse: () => 'Forgot my homework and made up an excuse',
      homeworkMadeUp: () => 'Did my missing homework at recess',
      lostFound: () => 'Lost my pencil case… and found it in the lost & found box',
      returnedItem: (who?: string) => `Gave ${who ?? 'a classmate'} back something they had lost`,
      calmExplained: () => 'Explained a misunderstanding calmly to Ms. Lin',
      shouted: () => 'Shouted at Ms. Lin over a misunderstanding',
      bullyStood: () => 'Stood next to Bo when older kids teased him',
      bullyTold: () => 'Told a grown-up when Bo was being teased',
      bullyIgnored: () => 'Walked away when Bo was being teased',
      contestWin: () => 'Won the class spelling bee!',
      contestSetback: () => 'Didn’t win the spelling bee — yet. I’ll keep practising.',
      quiz: (grade?: string) => `Pop quiz: grade ${grade ?? '?'}`,
      meeting: (how?: string) => (how === 'great' ? 'Parents’ meeting: Ms. Lin had lovely things to say' : how === 'mixed' ? 'Parents’ meeting: some good news, some things to work on' : 'Parents’ meeting: Mom and I made a plan together'),
    },

    /** Small talk and memory-based greetings for the five classmates. */
    kids: {
      xiaohua: {
        hello: ['Hi! Am I late? …Oh, phew. It’s not even class time.', 'Hey! Did you sleep well? I always sleep too well.'],
        topics: [
          { say: 'My grandpa says the early bird catches the worm. I say the early bird is very sleepy.', choices: [{ t: 'Haha! Try going to bed earlier?', reply: 'Mmm… maybe if I stop reading comics under the blanket.' }, { t: 'What do you like to read?', reply: 'Comics about space cats! The captain is a cat called Mochi.' }] },
          { say: 'I’m saving up for a rocket kit. It has a real launch pad!', choices: [{ t: 'Cool! Can I see it when you build it?', reply: 'Yes! You can count down: three, two, one…' }, { t: 'How much more do you need?', reply: 'Two more weeks of not buying candy. Ugh.' }] },
        ],
        react: {
          alarm: 'Guess what? I set two alarms and packed my bag last night. I was EARLY today! Thanks for the tip!',
          excuse: 'The excuse didn’t work… Ms. Lin knew. Next time I’ll just tell the truth.',
          teased: '…Oh. It’s you. Please don’t laugh at me again. It made me feel small.',
          madeUp: 'We’re good now. Thanks for saying sorry — that was cool of you.',
        },
        apologise: 'Say sorry for laughing at him',
        apologyReply: 'Okay… thanks. It’s fine now. Want to see my space-cat comic?',
      },
      xiaomei: {
        hello: ['Oh! Hi… I was just reading.', 'Hello. Do you like the library too?'],
        topics: [
          { say: 'I finished a book about a girl who talks to whales. I cried at the end. Is that weird?', choices: [{ t: 'Not weird at all. Good books do that.', reply: 'Thanks… I’ll lend it to you if you want.' }, { t: 'Whales can really “talk”, you know!', reply: 'Really? They sing songs that travel really far. I read that too!' }] },
          { say: 'I’m trying to be braver. Today I said “good morning” to the bus driver.', choices: [{ t: 'That’s brave! Little steps count.', reply: 'He said good morning back! I was so happy.' }, { t: 'Next, say it in English!', reply: 'Good… morning! Hehe. I did it.' }] },
        ],
        react: {
          asked: 'Ms. Lin helped me with question 3. I get it now! Asking was scary… but it was worth it.',
          copied: 'I still don’t get question 3… Copying didn’t help me learn. Next time can you just explain it to me?',
          skipped: 'I hid in the nurse’s room, but I wasn’t sick. I felt worse, not better.',
          madeUp: 'Thank you for saying sorry. Let’s study together sometime?',
        },
        apologise: 'Say sorry for the bad advice',
        apologyReply: 'It’s okay. We both learned something. Want to do homework together next time?',
      },
      azhe: {
        hello: ['Yo! Want to hear a joke? …Later. I’m practising self-control. Hehe.', 'Hey, hey! What’s up?'],
        topics: [
          { say: 'Why did the student eat his homework? Because the teacher said it was a piece of cake!', choices: [{ t: 'Haha! That’s terrible. I love it.', reply: 'I have a hundred more. I’ll save them for recess!' }, { t: 'Tell me another one!', reply: 'What has four wheels and flies? A rubbish truck! Hahaha!' }] },
          { say: 'I want to be a TV host when I grow up. Talking is my superpower!', choices: [{ t: 'You’d be great at it!', reply: 'Right? But a good host also knows when to listen. I’m learning that part.' }, { t: 'Superpowers need control, though.', reply: 'Ha, that’s what Ms. Lin says. You two should start a club.' }] },
        ],
        react: {
          calm: 'I raised my hand three times today and waited every time. Ms. Lin gave me a sticker!',
          clowned: 'We both got in trouble yesterday, huh. Let’s save the funny faces for recess.',
          mocked: 'Hmph. “Serves you right” really wasn’t nice, you know.',
          madeUp: 'All good, friend! Everyone says dumb stuff sometimes. Me most of all!',
        },
        apologise: 'Say sorry for being mean',
        apologyReply: 'Apology accepted! High five? ✋',
      },
      xiaoqiang: {
        hello: ['Hey! Want to play ball at recess?', 'Yo! I scored three goals at the field yesterday!'],
        topics: [
          { say: 'I’m going to be a pro football player. Coach says I need to pass more, though.', choices: [{ t: 'Passing makes the whole team better!', reply: 'Yeah, yeah… I’m working on it. Xiaofang is really good at passing, actually.' }, { t: 'Can you teach me a trick?', reply: 'Sure! Step on the ball, roll it back, turn. Easy!' }] },
          { say: 'My dad and I watch baseball every Sunday. He shouts at the TV. It’s so funny.', choices: [{ t: 'Who’s your favourite team?', reply: 'The Brothers! Yellow forever!' }, { t: 'Does the TV ever shout back?', reply: 'Haha! No, but Mom does!' }] },
        ],
        react: {
          madeUp: 'Me and Xiaofang take turns now. Five minutes each. Honestly? It’s more fun with two.',
          sideMe: 'Thanks for taking my side yesterday… but Xiaofang won’t talk to me now. That kind of sucks.',
          sideOther: 'You took Xiaofang’s side yesterday. I’m still upset about that.',
          grabbed: 'Not cool, running off with my ball. It’s not a toy for teasing.',
          madeUpYou: 'Okay, we’re cool again. Thanks for saying sorry.',
        },
        apologise: 'Say sorry and talk it over',
        apologyReply: 'Okay. I guess I wasn’t great either. Let’s all play together next time.',
      },
      xiaofang: {
        hello: ['Hi! Did you see the clouds this morning? One looked like a rabbit.', 'Hey! I’m practising my free kicks. Wanna watch?'],
        topics: [
          { say: 'People say football is a boys’ game. That’s silly. A ball doesn’t care who kicks it.', choices: [{ t: 'Totally! You’re really good.', reply: 'Thanks! I practise every morning before school.' }, { t: 'Let’s play together sometime!', reply: 'Deal! I’ll teach you my curved shot.' }] },
          { say: 'When I’m angry, I count to ten and squeeze my fists. Then I let go. It works… mostly.', choices: [{ t: 'That’s a great trick.', reply: 'My big sister taught me. She says feelings are like waves — they pass.' }, { t: 'I take deep breaths.', reply: 'Ooh, I’ll try that too. In… and out.' }] },
        ],
        react: {
          madeUp: 'Taking turns is way better than fighting. And Xiaoqiang said sorry! That never happens!',
          sideMe: 'Thanks for standing up for me. But… maybe next time help us both, okay?',
          sideOther: 'You took Xiaoqiang’s side. I really thought you were my friend.',
          grabbed: 'That wasn’t funny. We were already upset.',
          madeUpYou: 'Thanks for saying sorry. Friends again!',
        },
        apologise: 'Say sorry and talk it over',
        apologyReply: 'Okay. Thanks for coming back to say it. That takes guts.',
      },
    },

    /** Lines other characters say when they remember something or a big event happened. */
    react: {
      teacher: {
        honest: 'Thank you again for telling me the truth about your homework. Honesty makes everything easier.',
        calm: 'I’m still sorry about the mix-up the other day. You explained it so calmly — I learned from you.',
        helped: 'I’ve noticed how you help your classmates. That’s what a good friend does.',
      },
      bo: {
        stood: 'Thanks for standing next to me at the park. When I see those kids now, I feel braver.',
        told: 'Uncle Chun talked to those big kids. They said sorry! Thank you for telling him.',
        ignored: 'I saw you walk away at the park… It’s okay. Just… next time, maybe stay with me?',
      },
      mom: {
        meeting: 'I’m still smiling about what Ms. Lin said at the parents’ meeting.',
      },
      festival: (name: string) => `Happy ${name}! Isn’t this week fun?`,
      trip: (place: string) => `I heard you went to ${place}! What was your favourite part?`,
      typhoon: 'What a typhoon! Stay inside and stay safe, okay?',
      rain: 'So much rain today! Did you bring an umbrella?',
    },

    // --- Classroom cases --------------------------------------------------------------------
    cases: {
      late: {
        open: 'Oh no, oh no! I overslept and the bell already rang! Ms. Lin will be so angry… Maybe I’ll just hide in the restroom until recess?',
        good: 'Hiding makes it worse. Let’s go in and say sorry together.',
        excuse: 'Just tell her the bus was late.',
        tease: 'Haha! You’re SO late!',
        how: 'But… what do I even say?',
        howGood: 'Say sorry, tell the truth, and promise to try harder.',
        howQuiet: 'Sneak in quietly. Maybe she won’t notice.',
        quietReply: 'Sneaking feels scary… What if she sees me? I think saying it is better.',
        phraseIntro: 'Let’s practise it together, in English like Ms. Lin taught us:',
        plan: 'Okay, I’ll say it! But how do I stop being late next time?',
        planAlarm: 'Set an alarm and put the clock far from your bed.',
        planBag: 'Pack your bag the night before.',
        planBoth: 'Both! An alarm AND your bag ready by the door.',
        planReply: 'That’s a great plan. I’m going to try it tonight!',
        teacher: 'Thank you for telling me honestly, Xiaohua. Everyone is late sometimes — what matters is what we do next. Come and sit down.',
        thanks: 'Phew… That wasn’t so bad. Thanks for helping me!',
        excuseEnd: 'Xiaohua tells Ms. Lin the bus was late. She calmly says she saw his bus arrive on time. Now he has to explain twice… Excuses usually make things harder.',
        teaseEnd: 'Xiaohua looks at the floor and goes in alone. His ears are bright red. How would you feel?',
      },
      homework: {
        open: 'I… I didn’t finish my maths homework. I didn’t understand question 3, so I just… stopped. If I go in, everyone will see.',
        good: 'It’s okay not to understand. Let’s ask Ms. Lin for help, honestly.',
        copy: 'Quick, copy mine!',
        sick: 'Go to the nurse and say you feel sick.',
        scared: 'But what if she’s angry? What if I say something wrong?',
        brave: 'Saying “I don’t know” is brave. Teachers like questions.',
        withYou: 'I’ll come with you. You won’t be alone.',
        braveReply: 'Brave… okay. I can be brave.',
        withYouReply: 'Really? Thank you… that makes it easier.',
        phraseIntro: 'You could say it like this — in English, for practice:',
        teacher: 'Thank you for telling me, Xiaomei. Not understanding is how learning starts. Let’s look at question 3 together at recess.',
        thanks: 'She wasn’t angry at all! I’m glad I asked. Thank you for being my friend.',
        copyEnd: 'Xiaomei copies your answers. In class, Ms. Lin asks her to explain question 3… and she can’t. She looks at you, very quietly. Copying didn’t help her learn.',
        sickEnd: 'The nurse gently checks Xiaomei and says she isn’t sick. Xiaomei misses the lesson and still doesn’t understand question 3.',
      },
      standing: {
        open: 'Psst! Ms. Lin made me stand at the back because I kept talking. So unfair! Hey — want to see my funny face? 😜',
        good: 'Not now. Let’s not get in more trouble. Talk to me at recess!',
        clown: 'Make funny faces back.',
        mock: 'Serves you right.',
        why: '…Yeah, okay. I don’t mean to talk. I just get excited and the words pop out!',
        tipWrite: 'When an idea pops up, write it down and tell me later.',
        tipBreathe: 'Take a deep breath and raise your hand. Wait your turn.',
        tipReply: 'Huh. That could actually work. Like a secret joke notebook!',
        fix: 'Should I say something to Ms. Lin?',
        fixGood: 'Yes. Say sorry and tell her your plan.',
        fixLater: 'Maybe wait until she forgets.',
        fixLaterReply: 'Hmm… I think she won’t forget. Making it right is better. Let’s do it.',
        phraseIntro: 'Let’s say it together, in English:',
        teacher: 'Thank you, Azhe. I can see you’re really trying. Please sit down — and I’d love to hear your ideas when it’s your turn.',
        thanks: 'Yesss! Self-control: level one! Thanks, buddy.',
        clownEnd: 'Ms. Lin turns around and sees both of you pulling faces. “Two of you now?” she says calmly. You both stand until the bell. Not so funny anymore.',
        mockEnd: 'Azhe stops smiling. “Wow. Thanks a lot,” he mutters, and looks away.',
      },
      ball: {
        open: '“It’s MY ball! I brought it!” shouts Xiaoqiang.\n“But you’ve had it ALL recess! It’s my turn!” shouts Xiaofang.',
        calm: 'Hey, let’s calm down. Can each of you say how you feel?',
        sideQiang: '“Xiaoqiang’s right. It’s his ball.”',
        sideFang: '“Xiaofang’s right. Share!”',
        grab: 'Grab the ball and run!',
        iIntro: 'Try saying it like this: “I feel … when … because …”. Which one is an I-message?',
        iGood: '“I feel sad when I don’t get a turn, because I want to play too.”',
        iBad1: '“You’re SO selfish!”',
        iBad2: '“You always ruin everything!”',
        iBadReply: 'That one blames the other person — it starts a bigger fight. An I-message talks about YOUR feeling.',
        iReply: 'Xiaofang takes a breath and says it. Xiaoqiang blinks. “Oh… I didn’t know you felt sad.”',
        phraseIntro: 'Here’s that I-message in English — say it with her:',
        solve: 'So… what do we do now?',
        turns: 'Take turns: five minutes each.',
        together: 'Play together! Pass the ball.',
        solveReply: '“Okay… sorry I shouted,” says Xiaoqiang. “Sorry too,” says Xiaofang. “Want to play together?”',
        thanks: 'Thanks for helping us. That was better than fighting!',
        sideQiangEnd: 'Xiaoqiang grins and runs off with the ball. Xiaofang’s eyes fill with tears. “Fine. Whatever.” She walks away alone.',
        sideFangEnd: 'Xiaoqiang throws the ball down. “Fine, take it! You’re all against me!” He stomps off.',
        grabEnd: 'You run off with the ball. Nobody laughs. Xiaoqiang and Xiaofang are now upset at each other AND at you.',
      },
    },

    // --- School events --------------------------------------------------------------------------
    events: {
      forgotHomework: {
        ask: 'Good morning, everyone. Please hand in last week’s homework… Oh? You don’t have yours?',
        honest: 'I’m sorry, I forgot it.',
        excuse: 'My dog ate it!',
        honestReply: 'Thank you for being honest. That’s the most important thing. You can do it at recess, or bring it next time.',
        excuseReply: 'Hmm. I’d rather hear the truth — I won’t be cross. Did you forget?',
        admit: 'Yes… I forgot. Sorry.',
        admitReply: 'Thank you. See? The truth is lighter to carry.',
        makeUp: '📝 Do it now at recess',
        later: 'Bring it next time',
        doneReply: 'Well done! Thank you for making it right.',
      },
      lostPencil: {
        start: '😟 Oh no! Your pencil case isn’t in your bag. Maybe check the lost & found box in the corridor?',
        found: 'There it is — your pencil case, with your name on the label! Someone kind must have handed it in.',
        idle: 'A box of lost things: a lonely glove, a water bottle, three erasers…',
        other: (what: string, who: string) => `There’s ${what} with a name on it: “${who}”.`,
        give: (who: string) => `Take it to ${who}`,
        leave: 'Leave it',
        thanks: 'My missing thing! Thank you so much for bringing it to me!',
        items: { eraser: 'a dinosaur eraser', bottle: 'a blue water bottle', ruler: 'a sparkly ruler', bookmark: 'a cat bookmark', keychain: 'a little bear keychain' },
        carrying: (what: string, who: string) => `You’re carrying ${what} for ${who}.`,
      },
      misunderstanding: {
        ask: 'Someone spilled paint water in the art room and left it. Was it you? I’m disappointed.',
        calm: 'Stay calm and explain.',
        shout: 'That’s SO unfair! You always blame me!',
        silent: 'Say nothing and sulk.',
        explain: 'I was in the library then, not the art room. But I can help clean it up.',
        calmReply: 'Oh… you’re right, the librarian saw you. I’m sorry I jumped to conclusions. Thank you for explaining so calmly — and for offering to help.',
        shoutReply: 'I understand you’re upset. Shouting makes it hard for me to listen, though. Can you tell me calmly?',
        silentReply: 'I can’t know what happened if you don’t tell me. Take a breath — I’m listening.',
        tryCalm: 'Take a breath and explain calmly.',
      },
      bullying: {
        start: '😢 You hear someone crying near the kite tree in the park…',
        open: 'Two older kids are laughing at Bo’s kite. “Baby kite! Baby kite!” Bo is holding back tears.',
        stand: 'Stand next to Bo and say calmly: “Stop, please. That’s not kind.”',
        tell: 'Go and tell a grown-up (Uncle Chun or Bo’s mom).',
        fight: 'Push them!',
        ignore: 'Walk away and pretend you didn’t see.',
        fightReply: 'Fighting makes things worse — someone could get hurt, and then YOU are in trouble too. There’s a braver way.',
        standReply: 'The big kids look surprised. “Whatever,” they mumble, and leave. Bo wipes his eyes. “You… stayed. Thank you.”',
        tellReply: 'A grown-up comes over and talks to the older kids quietly. They say sorry to Bo. Telling isn’t tattling — it’s getting help.',
        ignoreReply: 'You walk away. Behind you, the laughing keeps going. Your tummy feels heavy all afternoon.',
        boThanks: 'You’re a real friend. Want to fly my kite with me?',
      },
      contest: {
        notice: '🐝 Class spelling bee at 2 p.m.! Come to the notice board to join.',
        intro: 'Welcome to the class spelling bee! Three words. Listen carefully and pick the right spelling.',
        join: '🐝 Join the spelling bee',
        notYet: 'The spelling bee starts at 2 p.m.',
        over: 'The spelling bee is over for today.',
        word: (n: number) => `Word ${n} of 3. Listen… 🔊`,
        hear: '🔊 Hear it again',
        right: '✅ Correct!',
        wrong: (w: string) => `❌ Oops — it’s spelled “${w}”.`,
        win: '🏅 You won the spelling bee! Ms. Lin pins a gold bee on your shirt. Everybody claps!',
        setback: 'You came fourth this time. Your friend Xiaomei won with “necessary”.',
        mindsetQ: 'Ms. Lin: “How do you feel about it?”',
        mindsetGood: 'I’ll practise and try again next time!',
        mindsetBad: 'I’m just bad at spelling.',
        mindsetBadReply: 'You’re not bad at spelling — you’re not good at it YET. Every expert was once a beginner. What could you try next time?',
        mindsetReply: 'That’s the spirit! Practising a little every day really works.',
        winReply: 'Congratulations! Remember: you got here by practising. Keep it up!',
        /** ENGLISH PRACTICE: spelling words; keep in English. */
        words: ['because', 'friend', 'beautiful', 'library', 'Wednesday', 'different', 'tomorrow', 'favourite', 'believe', 'together', 'surprise', 'answer'],
      },
      quiz: {
        start: 'Pop quiz time! Don’t worry — it’s just to see what we’ve learned. Do your best!',
        go: '📝 Start the quiz',
        result: (g: string) => (g === 'A' ? 'Excellent work! An A!' : g === 'B' ? 'Good job — a B! A little more practice and you’ll fly.' : 'A C this time. That just shows us what to practise. We’ll get there together.'),
      },
      parentsMeeting: {
        notice: '👨‍👩‍👧 Parents’ meeting this evening! Mom will meet Ms. Lin after school.',
        momIntro: 'I’m back from the parents’ meeting (家長會). Ms. Lin told me all about you…',
        great: 'She said you’re kind and you help your classmates. She told me about these moments:',
        proud: 'I’m so proud of you!',
        study: 'She also said you work hard at your studies. Keep it up!',
        studyLow: 'She said you could focus a little more on studying. Shall we make a plan together?',
        unkind: 'She mentioned that once you weren’t very kind to a classmate. How do you think they felt?',
        reflect: 'They probably felt sad. I’ll say sorry.',
        reflectReply: 'That’s my kid. Saying sorry is brave.',
        thanksMom: 'Thanks, Mom! ❤️',
        plan: 'Let’s make a study plan together.',
        planReply: 'Good. Twenty minutes every evening — and I’ll make tea. Deal?',
        nothing: 'She said you’re a lovely kid, and she’d love to see more of what you can do!',
        done: 'Mom gives you a big hug.',
      },
    },
  },
};
